import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Settings, 
  Building2, 
  Palette, 
  Globe, 
  MapPin, 
  Mail, 
  FileText, 
  CreditCard, 
  Brain, 
  FileCheck,
  Users,
  UserCog,
  Shield,
  Lock,
  Crown,
  Layers,
  Gauge,
  Bell,
  AlertTriangle,
  Clock,
  Key,
  Webhook,
  Beaker,
  ArrowRight,
  Wrench,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

/**
 * Platform Settings Overview - Landing page for settings workspace
 */
const PlatformSettingsOverview = () => {
  const navigate = useNavigate();

  const settingsCategories = [
    {
      title: 'General',
      description: 'Platform profile, branding, and regional settings',
      icon: Settings,
      color: 'amber',
      items: [
        { label: 'Platform Profile', path: '/platform/settings/profile', status: 'available' },
        { label: 'Platform Branding', path: '/platform/settings/branding', status: 'available' },
        { label: 'Regional Defaults', path: '/platform/settings/regional', status: 'available' },
      ]
    },
    {
      title: 'Plans & Entitlements',
      description: 'Membership plans, entitlements, and role templates',
      icon: Crown,
      color: 'amber',
      items: [
        { label: 'All Plans', path: '/platform/settings/plans', status: 'configured' },
        { label: 'Entitlements', path: '/platform/settings/plans/entitlements', status: 'available' },
        { label: 'Role Templates', path: '/platform/settings/plans/role-templates', status: 'available' },
      ]
    },
    {
      title: 'Integrations',
      description: 'Third-party services and API connections',
      icon: Wrench,
      color: 'blue',
      items: [
        { label: 'Google Address Services', path: '/platform/settings/integrations/google-address', status: 'configured' },
        { label: 'Mailgun', path: '/platform/settings/integrations/mailgun', status: 'available' },
        { label: 'PostGrid', path: '/platform/settings/integrations/postGrid', status: 'available' },
        { label: 'Whop Integration', path: '/platform/settings/integrations/whop', status: 'available' },
        { label: 'Stripe', path: '/platform/settings/integrations/stripe', status: 'available' },
        { label: 'OpenAI', path: '/platform/settings/integrations/openai', status: 'configured' },
        { label: 'Credit Report Providers', path: '/platform/settings/integrations/credit-providers', status: 'available' },
      ]
    },
    {
      title: 'Users & Access',
      description: 'Roles, permissions, and access control',
      icon: Users,
      color: 'purple',
      items: [
        { label: 'Roles & Permissions', path: '/platform/settings/roles', status: 'available' },
        { label: 'Admin Access Rules', path: '/platform/settings/admin-rules', status: 'available' },
        { label: 'Security Policies', path: '/platform/settings/security-policies', status: 'available' },
      ]
    },
    {
      title: 'Plans Configuration',
      description: 'Plan defaults, usage limits, and entitlement rules',
      icon: Layers,
      color: 'emerald',
      items: [
        { label: 'Plan Defaults', path: '/platform/settings/plan-defaults', status: 'available' },
        { label: 'Entitlement Rules', path: '/platform/settings/entitlement-rules', status: 'available' },
        { label: 'Usage Limits', path: '/platform/settings/usage-limits', status: 'available' },
        { label: 'Role Templates', path: '/platform/settings/role-templates', status: 'available' },
      ]
    },
    {
      title: 'Communication',
      description: 'Email providers and notification settings',
      icon: Mail,
      color: 'cyan',
      items: [
        { label: 'Email Provider', path: '/platform/settings/email-provider', status: 'available' },
        { label: 'Notification Templates', path: '/platform/settings/notification-templates', status: 'available' },
        { label: 'System Alerts', path: '/platform/settings/system-alerts', status: 'available' },
      ]
    },
    {
      title: 'Security',
      description: 'Authentication, sessions, and audit logs',
      icon: Shield,
      color: 'red',
      items: [
        { label: 'Authentication', path: '/platform/settings/authentication', status: 'available' },
        { label: 'Sessions', path: '/platform/settings/sessions', status: 'available' },
        { label: 'Audit Logs', path: '/platform/settings/audit-logs', status: 'available' },
      ]
    },
    {
      title: 'Advanced',
      description: 'API keys, webhooks, and feature flags',
      icon: Beaker,
      color: 'orange',
      items: [
        { label: 'API Keys', path: '/platform/settings/api-keys', status: 'available' },
        { label: 'Webhooks', path: '/platform/settings/webhooks', status: 'available' },
        { label: 'Feature Flags', path: '/platform/settings/feature-flags', status: 'available' },
        { label: 'Labs', path: '/platform/settings/labs', status: 'available' },
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', icon: 'text-amber-500' },
      blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', icon: 'text-blue-500' },
      purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', icon: 'text-purple-500' },
      emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: 'text-emerald-500' },
      cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', icon: 'text-cyan-500' },
      red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', icon: 'text-red-500' },
      orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400', icon: 'text-orange-500' },
    };
    return colors[color] || colors.amber;
  };

  const getStatusBadge = (status) => {
    if (status === 'configured') {
      return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Configured</Badge>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0B1F18] text-white p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Platform Settings</h1>
        <p className="text-[#93AE9F]">Configure platform-wide settings, integrations, and system defaults</p>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {settingsCategories.map((category) => {
          const Icon = category.icon;
          const colors = getColorClasses(category.color);
          
          return (
            <Card 
              key={category.title} 
              className={`bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-all`}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${colors.bg}`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <div>
                    <CardTitle className="text-white">{category.title}</CardTitle>
                    <CardDescription className="text-zinc-400">{category.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {category.items.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-all group"
                    >
                      <span className="text-sm text-zinc-300 group-hover:text-white">{item.label}</span>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(item.status)}
                        <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformSettingsOverview;
